import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "sonner";
import { debounce } from "lodash";
import CustomDropdown from "./CustomDropdown";
import nodeApi from "../lib/api";
import axios from "axios";

const forFactory = {
  factorySource: "ALCLAD",
  factoryDestination:
    "PS Pace 10TH FLOOR, UNIT- 1003 1/1A MAHENDRA ROY LANE KOLKATA – 700046",
};

const initialChallan = () => ({
  ic_ch_no: "",
  ic_ch_date: "",
  ic_from: "Coaters",
  ic_source_name: "",
  ic_source_id: "",
  ic_source_address: "",
  ic_to: "Factory",
  ic_destination_name: "",
  ic_destination_id: "",
  ic_destination_address: "",
  items: [
    {
      item_code: "",
      alclad_code: "",
      item_description: "",
      size: "",
      quantity: "",
      ic_color_shade: "",
    },
  ],
  ref_og_ch_no: "",
  remarks: "",
});

function EditIncomingDialog({ show, onClose, challan }) {
  const [challanData, setChallanData] = useState(initialChallan());
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const queryClient = useQueryClient();
  const alerpApiKey = process.env.REACT_APP_ALERP_API_KEY;

  // Fetch coaters
  const { data: coaters, isLoading: coatersLoading } = useQuery({
    queryKey: ["coaters"],
    queryFn: async () => {
      const res = await nodeApi.get("/coater");
      return res.data;
    },
  });

  // Fetch sites
  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await nodeApi.get("/site");
      return res.data?.sites;
    },
  });

  // Fetch outgoing challans
  const { data: outgoingData = [], isLoading: outgoingLoading } = useQuery({
    queryKey: ["outgoing"],
    queryFn: async () => {
      const res = await nodeApi.get("/outgoing");
      return res.data;
    },
    onError: (err) => {
      toast.error(`Failed to fetch challans: ${err.message}`);
    },
  });

  // Fetch item names from ALERP API
  const itemApiUrl =
    process.env.REACT_APP_ALERP_API_URL || "https://alcad-erp-api.sirpify.com";
  const { data: itemNames = [], isLoading: itemNamesLoading } = useQuery({
    queryKey: ["itemNames"],
    queryFn: async () => {
      try {
        const res = await axios.get(`${itemApiUrl}/item_names_challan`, {
          headers: { "X-API-Key": alerpApiKey },
        });
        let items = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          items = res.data.data;
        } else if (Array.isArray(res.data)) {
          items = res.data;
        } else if (res.data?.items && Array.isArray(res.data.items)) {
          items = res.data.items;
        } else if (res.data?.results && Array.isArray(res.data.results)) {
          items = res.data.results;
        }

        const normalizedItems = items.map((item, idx) => {
          const itemCode =
            item.item_code ||
            item.code ||
            item.ItemCode ||
            item.itemCode ||
            item.item_ref_code ||
            item.ref_code ||
            item.id ||
            "-";

          const itemDescription =
            item.item_desc ||
            item.item_description ||
            item.description ||
            item.ItemDescription ||
            item.itemDescription ||
            item.desc ||
            item.name ||
            item.item_name ||
            item.itemName ||
            "-";

          const itemRefType =
            item.item_ref_type ||
            item.ref_type ||
            item.ItemRefType ||
            item.itemRefType ||
            item.type ||
            "-";
          const alcladCode = item.alclad_code || "-";

          const normalized = {
            ...item,
            item_code: String(itemCode || "").trim(),
            alclad_code: String(alcladCode || "").trim(),
            item_description: String(itemDescription || "").trim(),
            item_ref_type: String(itemRefType || "").trim(),
            item_desc: String(item.item_desc || itemDescription || "").trim(),
          };
          return normalized;
        });
        // Removed filter to show all data including items with "-"

        return normalizedItems;
      } catch (error) {
        console.error("Error fetching item names:", error);
        console.error("Error response:", error.response?.data);
        throw error;
      }
    },
    onError: (err) => {
      toast.error(`Failed to fetch item names: ${err.message}`);
    },
  });

  // Pre-populate form with existing challan data
  useEffect(() => {
    if (show && challan) {
      setIsDirty(false);
      const updatedChallanData = {
        ...initialChallan(),
        ...challan,
        ic_ch_date: challan.ic_ch_date
          ? new Date(challan.ic_ch_date).toISOString().split("T")[0]
          : "",
        ic_destination_id: String(challan.ic_destination_id || ""),
        items:
          challan?.incoming_items?.length > 0
            ? challan.incoming_items.map((item) => ({
                ...item,
                item_code: item.item_code || "",
                alclad_code: item.alclad_code || "",
                item_description: item.item_description || "",
                quantity: String(item.quantity),
              }))
            : initialChallan().items,
      };

      if (updatedChallanData.ic_to === "Factory") {
        updatedChallanData.ic_destination_name = forFactory.factorySource;
        updatedChallanData.ic_destination_address =
          forFactory.factoryDestination;
        updatedChallanData.ic_destination_id = "";
      } else if (updatedChallanData.ic_to === "Site") {
        const selectedSite = sites?.find(
          (s) => s.id === Number(updatedChallanData.ic_destination_id)
        );
        if (selectedSite) {
          updatedChallanData.ic_destination_name = selectedSite.name || "";
          updatedChallanData.ic_destination_address =
            selectedSite.address || "";
        } else {
          updatedChallanData.ic_destination_name =
            challan.ic_destination_name || "";
          updatedChallanData.ic_destination_address =
            challan.ic_destination_address || "";
        }
      }

      setChallanData(updatedChallanData);
      setErrors({});
    }
  }, [show, challan, sites]);

  // Update challan mutation
  const updateChallanMutation = useMutation({
    mutationFn: async (updatedChallan) => {
      const payload = {
        ...updatedChallan,
        ic_destination_id: String(updatedChallan.ic_destination_id),
        items: updatedChallan.items.map((item) => ({
          ...item,
          quantity: Number(item.quantity),
        })),
      };
      const res = await nodeApi.put(`/incoming/${payload.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Challan updated successfully!");
      queryClient.invalidateQueries(["incoming"]);
      onClose();
    },
    onError: (err) => {
      if (err.response?.data && typeof err.response.data === "object") {
        const backendErrors = err.response.data;
        const newErrors = {};
        const itemErrors = [];

        for (const [key, value] of Object.entries(backendErrors)) {
          if (key.startsWith("items.")) {
            const parts = key.split(".");
            const index = parseInt(parts[1], 10);
            const field = parts[2];
            while (itemErrors.length <= index) {
              itemErrors.push({});
            }
            if (value && value.length > 0) {
              itemErrors[index][field] = value[0];
            }
          } else {
            if (value && value.length > 0) {
              newErrors[key] = value[0];
            }
          }
        }
        if (itemErrors.length > 0) {
          newErrors.items = itemErrors;
        }
        setErrors(newErrors);
        toast.error("Please fill all the required fields.");
      } else {
        toast.error("Please fill all the required fields.");
      }
    },
  });

  const validate = () => {
    const newErrors = {};
    if (!challanData.ic_ch_no.trim())
      newErrors.ic_ch_no = "Challan No is required";
    if (!challanData.ic_ch_date)
      newErrors.ic_ch_date = "Challan Date is required";
    if (!challanData.ic_source_name.trim())
      newErrors.ic_source_name = "Source Name is required";
    if (!challanData.ic_destination_name.trim())
      newErrors.ic_destination_name = "Destination Name is required";
    if (!challanData.ref_og_ch_no.trim()) {
      newErrors.ref_og_ch_no = "Referral Outgoing Challan No. is required";
    } else if (
      !outgoingData?.some(
        (og) => String(og.og_ch_no) === String(challanData.ref_og_ch_no)
      )
    ) {
      newErrors.ref_og_ch_no =
        "Please select a valid Outgoing Challan No. from the dropdown";
    }

    const itemErrors = challanData.items.map((item, idx) => {
      const err = {};
      if (!item.item_code?.trim())
        err.item_code = `Item ${idx + 1}: Item Code required`;
      if (!item.alclad_code?.trim())
        err.alclad_code = `Item ${idx + 1}: Alclad Code required`;
      if (!item.item_description?.trim())
        err.item_description = `Item ${idx + 1}: Description required`;
      if (!item.size?.trim()) err.size = `Item ${idx + 1}: Size required`;
      if (!item.quantity) err.quantity = `Item ${idx + 1}: Quantity required`;
      if (!item.ic_color_shade?.trim())
        err.ic_color_shade = `Item ${idx + 1}: Color required`;
      return err;
    });

    if (itemErrors.some((err) => Object.keys(err).length > 0)) {
      newErrors.items = itemErrors;
    }
    return newErrors;
  };
  

  const handleSaveClick = debounce(() => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all the required fields.");
      return;
    }
    updateChallanMutation.mutate(challanData);
  }, 300);

  const getInputClass = (field) =>
    errors[field] ? "form-control is-invalid" : "form-control";
  const getItemInputClass = (idx, field) =>
    errors.items?.[idx]?.[field] ? "form-control is-invalid" : "form-control";

  const handleItemChange = (index, field, value) => {
    const rawValue = value ?? "";
    let sanitizedValue = String(rawValue);
    if (field === "quantity") {
      sanitizedValue = sanitizedValue.replace(/[^\d]/g, "");
    }
    if (field === "size") {
      sanitizedValue = sanitizedValue.replace(/[^0-9.]/g, "");
      sanitizedValue = sanitizedValue.replace(/(\..*)\./g, "$1");
    }
    const trimmedValue = sanitizedValue.trim();

    setChallanData((prev) => {
      setIsDirty(true);
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: trimmedValue };

          // Auto-populate/reset item_description and alclad_code when item_code changes
          if (field === "item_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const code = String(itemOption.item_code || "").trim();
                return code === trimmedValue && code !== "";
              });
              if (selectedItem) {
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_description = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_description = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and alclad_code when item_description changes
          if (field === "item_description") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const itemDesc = String(
                  itemOption.item_desc || itemOption.item_description || ""
                ).trim();
                return itemDesc === trimmedValue && itemDesc !== "";
              });
              if (selectedItem && selectedItem.item_code) {
                updatedItem.item_code = String(selectedItem.item_code).trim();
                const alcladCode = selectedItem.alclad_code || "";
                updatedItem.alclad_code = String(alcladCode || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.alclad_code = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.alclad_code = "";
            }
          }

          // Auto-populate/reset item_code and item_description when alclad_code changes
          if (field === "alclad_code") {
            if (trimmedValue) {
              const selectedItem = itemNames?.find((itemOption) => {
                const alcladCode = String(itemOption.alclad_code || "").trim();
                return alcladCode === trimmedValue && alcladCode !== "";
              });
              if (selectedItem) {
                if (selectedItem.item_code) {
                  updatedItem.item_code = String(selectedItem.item_code).trim();
                }
                const desc =
                  selectedItem.item_desc || selectedItem.item_description || "";
                updatedItem.item_description = String(desc || "").trim();
              } else {
                updatedItem.item_code = "";
                updatedItem.item_description = "";
              }
            } else {
              updatedItem.item_code = "";
              updatedItem.item_description = "";
            }
          }

          return updatedItem;
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });
    if (errors.items?.[index]?.[field]) {
      setErrors((prev) => {
        const newItemsErrors = [...(prev.items || [])];
        if (newItemsErrors[index]) {
          delete newItemsErrors[index][field];
        }
        return { ...prev, items: newItemsErrors };
      });
    }
  };

  const handleChange = (field, value) => {
    setChallanData((prev) => {
      setIsDirty(true);
      const updated = { ...prev, [field]: value };

      if (field === "ic_to") {
        if (value === "Factory") {
          updated.ic_destination_name = forFactory.factorySource;
          updated.ic_destination_address = forFactory.factoryDestination;
          updated.ic_destination_id = "";
        } else {
          updated.ic_destination_name = "";
          updated.ic_destination_address = "";
          updated.ic_destination_id = "";
        }
      }

      if (field === "ic_source_id") {
        if (value) {
          const selectedCoater = coaters?.find(
            (c) => String(c.coater_id) === String(value)
          );
          if (selectedCoater) {
            updated.ic_source_name = selectedCoater.name;
            updated.ic_source_address = selectedCoater.address;
          }
        } else {
          updated.ic_source_id = "";
          updated.ic_source_name = "";
          updated.ic_source_address = "";
        }
      }

      if (field === "ic_destination_id") {
        const selectedSite = sites?.find((s) => s.id === Number(value));
        if (selectedSite) {
          updated.ic_destination_name = selectedSite.name || "";
          updated.ic_destination_address = selectedSite.address || "";
          updated.ic_destination_id = String(value);
        } else {
          updated.ic_destination_name = "";
          updated.ic_destination_address = "";
          updated.ic_destination_id = "";
        }
      }

      return updated;
    });

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmLeave = () => {
    setShowCloseConfirm(false);
    setIsDirty(false);
    onClose();
  };

  const cancelLeave = () => {
    setShowCloseConfirm(false);
  };

  const areAllItemsFilled = () => {
    if (!Array.isArray(challanData.items) || challanData.items.length === 0)
      return false;
    return challanData.items.every(
      (item) =>
        (item?.item_code || "").trim() &&
        (item?.alclad_code || "").trim() &&
        (item?.item_description || "").trim() &&
        (item?.size || "").trim() &&
        item?.quantity &&
        (item?.ic_color_shade || "").trim()
    );
  };

  const handleAddItem = () => {
    setChallanData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item_code: "",
          alclad_code: "",
          item_description: "",
          size: "",
          quantity: "",
          ic_color_shade: "",
        },
      ],
    }));
  };

  const handleDeleteItem = (index) => {
    setChallanData((prev) => {
      if (prev.items.length === 1) {
        return {
          ...prev,
          items: [
            {
              item_code: "",
              alclad_code: "",
              item_description: "",
              size: "",
              quantity: "",
              ic_color_shade: "",
            },
          ],
        };
      }
      return { ...prev, items: prev.items.filter((_, i) => i !== index) };
    });
  };

  // const handleClear = () => {
  //   setChallanData(initialChallan());
  //   setErrors({});
  // };
  const isAddDisabled = !areAllItemsFilled();

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleRequestClose} size="xl" dialogClassName="dialog-inline" centered>
        <style>
    {`
      .dialog-inline {
        max-width: 95% !important;
        width: 95% !important;
      }`}
    </style>
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>Edit Incoming Challan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan No <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className={getInputClass("ic_ch_no")}
                  value={challanData.ic_ch_no}
                  onChange={(e) => handleChange("ic_ch_no", e.target.value)}
                  readOnly
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  Challan Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  className={getInputClass("ic_ch_date")}
                  value={challanData.ic_ch_date}
                  onChange={(e) => handleChange("ic_ch_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="text"
                  value={challanData.ic_from}
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To</Form.Label>
                <Form.Select
                  value={challanData.ic_to}
                  onChange={(e) => handleChange("ic_to", e.target.value)}
                >
                  <option value="Factory">Factory</option>
                  <option value="Site">Site</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Source Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Source Name <span className="text-danger">*</span>
                </Form.Label>
                <CustomDropdown
                  options={coaters}
                  value={challanData.ic_source_id}
                  onChange={(value) => handleChange("ic_source_id", value)}
                  placeholder="Search coaters..."
                  isLoading={coatersLoading}
                  error={errors.ic_source_name}
                  idField="coater_id"
                  nameField="name"
                  initialName={challanData.ic_source_name}
                  isClearable={true}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Source Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={challanData.ic_source_address}
                  onChange={(e) =>
                    handleChange("ic_source_address", e.target.value)
                  }
                  disabled={!!challanData.ic_source_id}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3">Destination Details</h6>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Destination Name <span className="text-danger">*</span>
                </Form.Label>
                {challanData.ic_to === "Site" ? (
                  <CustomDropdown
                    options={sites || []}
                    value={challanData.ic_destination_id}
                    onChange={(value) =>
                      handleChange("ic_destination_id", value)
                    }
                    placeholder="Search sites..."
                    isLoading={sitesLoading}
                    error={errors.ic_destination_name}
                    idField="id"
                    nameField="name"
                    initialName={challanData.ic_destination_name}
                    isClearable={true}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    className={getInputClass("ic_destination_name")}
                    value={challanData.ic_destination_name}
                    onChange={(e) =>
                      handleChange("ic_destination_name", e.target.value)
                    }
                    disabled={challanData.ic_to === "Factory"}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Destination Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={challanData.ic_destination_address}
                  onChange={(e) =>
                    handleChange("ic_destination_address", e.target.value)
                  }
                  disabled={
                    challanData.ic_to === "Factory" ||
                    (challanData.ic_to === "Site" &&
                      !!challanData.ic_destination_id)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <hr className="mt-4" />
          <h6>
            Items <span className="text-danger">*</span>
          </h6>
          {challanData.items.map((item, idx) => (
            <div key={idx}>
              <Row className="g-2 mb-2">
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                     Supplier Item Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "item_code", selectedValue);
                      }}
                      placeholder="Select Item Code..."
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_code}
                      idField="item_code"
                      nameField="item_code"
                      displayFormat={(option) =>
                        String(option.item_code || "").trim()
                      }
                      isClearable={true}
                    />

                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      ALCLAD Code <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.alclad_code || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(idx, "alclad_code", selectedValue);
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.alclad_code || "").trim()
                          ? "NA"
                          : "Select Alclad Code..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.alclad_code}
                      idField="alclad_code"
                      nameField="alclad_code"
                      displayFormat={(option) => String(option.alclad_code || "")}
                      isClearable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <CustomDropdown
                      options={itemNames || []}
                      value={item.item_description || ""}
                      onChange={(selectedValue) => {
                        handleItemChange(
                          idx,
                          "item_description",
                          selectedValue
                        );
                      }}
                      placeholder={
                        (item.item_code || "").trim() &&
                        !(item.item_description || "").trim()
                          ? "NA"
                          : "Select Description..."
                      }
                      isLoading={itemNamesLoading}
                      error={errors.items?.[idx]?.item_description}
                      idField="item_desc"
                      nameField="item_desc"
                      displayFormat={(option) => {
                        const desc =
                          option.item_desc || option.item_description || "";
                        return String(desc).trim();
                      }}
                      isClearable={true}
                    />

                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Size <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      step="any"
                      placeholder="Size"
                      className={getItemInputClass(idx, "size")}
                      value={item.size}
                      onChange={(e) =>
                        handleItemChange(idx, "size", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Qty <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      placeholder="Qty"
                      className={getItemInputClass(idx, "quantity")}
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(idx, "quantity", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-0">
                    <Form.Label
                      className="mb-1"
                      style={{
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        lineHeight: "1",
                      }}
                    >
                      Color Shade <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Color Shade"
                      className={getItemInputClass(idx, "ic_color_shade")}
                      value={item.ic_color_shade}
                      onChange={(e) =>
                        handleItemChange(idx, "ic_color_shade", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={1} className="d-flex align-items-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteItem(idx)}
                    style={{ height: "38px", width: "100%" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                      <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 L4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3V2h11v1h-11z" />
                    </svg>
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
          {isAddDisabled ? (
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="add-item-disabled-tooltip">
                  Please fill the required fields to  add a new item
                </Tooltip>
              }
            >
              <span className="d-inline-block mt-2" style={{ cursor: "not-allowed" }}>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleAddItem}
                  disabled
                  style={{ pointerEvents: "none" }}
                >
                  + Add Item
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="mt-2"
              onClick={handleAddItem}
            >
              + Add Item
            </Button>
          )}


          <hr className="mt-4" />
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>
                  Referral Outgoing Challan No.{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <CustomDropdown
                  options={outgoingData}
                  value={challanData.ref_og_ch_no}
                  onChange={(value) => handleChange("ref_og_ch_no", value)}
                  placeholder="Select Outgoing Challan..."
                  isLoading={outgoingLoading}
                  error={errors.ref_og_ch_no}
                  idField="og_ch_no"
                  nameField="og_ch_no"
                  initialName={challanData.ref_og_ch_no}
                  isClearable={true}
                  displayFormat={(option) => `${option.og_ch_no}`}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={challanData.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {/* <Button
          variant="warning"
          onClick={handleClear}
          disabled={updateChallanMutation.isLoading}
        >
          Clear
        </Button> */}
        <Button
          variant="secondary"
          onClick={handleRequestClose}
          disabled={updateChallanMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSaveClick}
          disabled={updateChallanMutation.isLoading}
        >
          {updateChallanMutation.isLoading ||
          updateChallanMutation.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>

      {/* Leave confirmation dialog */}
      <Modal show={showCloseConfirm} onHide={cancelLeave} centered>
        <Modal.Header closeButton>
          <Modal.Title>Leave Form?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Changes you made may not be saved.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelLeave}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmLeave}>
            Leave
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}

export default EditIncomingDialog;
